import type { OverrideInvoiceConflict } from '../../../api/customers';

/**
 * Mirror of packages/api/src/modules/finance/override-checks.ts, so the
 * customer page can flag the conflict against any API build. Keep the two in step.
 *
 * An MRR-sheet override checked against what was actually invoiced for the
 * contract it replaces. The sheet is Walter's data and wins the headline, but
 * when it and the invoice disagree by more than OVERRIDE_INVOICE_TOLERANCE the
 * page has to say so: ISS North America's override counts 54,000 USD a year
 * (€3,807/mo) while INV/2026/00043 bills 84,525 USD for the same contract.
 *
 * Only an override that runs (nearly) the whole term is compared: a tiered
 * contract whose override covers some months (Swiss GRC, Tier 2 for Oct–Feb)
 * says nothing about the yearly total and is left alone. Pure.
 */

export const OVERRIDE_INVOICE_TOLERANCE = 0.1;

export interface OverrideCheckInput {
  override: { mrr: number; from: string; to: string | null };
  /** The one live order the override replaces, YYYY-MM-DD. */
  order: { start: string | null; end: string | null };
  invoices: Array<{
    invoiceNumber: string;
    /** YYYY-MM-DD (a longer ISO string is cut). */
    issueDate: string;
    /** EUR, signed. */
    total: number;
    documentTotal: number;
    documentCurrency: string;
    isCreditNote: boolean;
    /** Cleared by a credit note: not billed. */
    credited: boolean;
  }>;
}


const monthOf = (iso: string) => iso.slice(0, 7);

function monthsBetween(fromMonth: string, toMonth: string): string[] {
  const out: string[] = [];
  let [y, m] = fromMonth.split('-').map(Number) as [number, number];
  for (let guard = 0; guard < 60; guard++) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (key > toMonth) break;
    out.push(key);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function overrideInvoiceConflict(input: OverrideCheckInput): OverrideInvoiceConflict | null {
  const { override, order } = input;
  if (!order.start || !order.end) return null;
  const start = order.start.slice(0, 10);
  const end = order.end.slice(0, 10);
  const days = (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1;
  const termMonths = Math.round(days / 30.44);
  if (termMonths < 1) return null;

  const termMonthKeys = monthsBetween(monthOf(start), monthOf(end));
  const covered = termMonthKeys.filter((m) => m >= override.from && (override.to === null || m <= override.to)).length;
  if (covered < termMonths - 1) return null;

  const earliest = new Date(Date.parse(start) - 31 * 86_400_000).toISOString().slice(0, 10);
  const billed = input.invoices.filter((i) => {
    const day = i.issueDate.slice(0, 10);
    return !i.isCreditNote && !i.credited && i.total > 0 && day >= earliest && day <= end;
  });
  const invoicedTotal = billed.reduce((sum, i) => sum + i.total, 0);
  if (invoicedTotal <= 0) return null;

  const overrideTotal = override.mrr * termMonths;
  const gapShare = (invoicedTotal - overrideTotal) / invoicedTotal;
  if (Math.abs(gapShare) <= OVERRIDE_INVOICE_TOLERANCE) return null;

  const currencies = new Set(billed.map((i) => i.documentCurrency));
  const documentCurrency = currencies.size === 1 ? [...currencies][0]! : 'EUR';
  const invoicedInDocument =
    documentCurrency === 'EUR' ? invoicedTotal : billed.reduce((sum, i) => sum + i.documentTotal, 0);
  const rate = invoicedInDocument / invoicedTotal;
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    termMonths,
    overrideTotal: round(overrideTotal),
    invoicedTotal: round(invoicedTotal),
    documentCurrency,
    overrideInDocument: round(overrideTotal * rate),
    invoicedInDocument: round(invoicedInDocument),
    invoiceNumbers: billed.map((i) => i.invoiceNumber),
    gapShare: Math.round(gapShare * 1000) / 1000,
  };
}

/** "The MRR sheet counts 54,000 USD for the 12-month term; INV/2026/00043 bills 84,525 USD (57% more). Check the MRR sheet." */
export function overrideConflictSentence(c: OverrideInvoiceConflict): string {
  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: c.documentCurrency, maximumFractionDigits: 0 }).format(n);
  const more = c.invoicedTotal > c.overrideTotal;
  const pct = Math.round(Math.abs(c.invoicedTotal / c.overrideTotal - 1) * 100);
  const docs = c.invoiceNumbers.length === 1 ? c.invoiceNumbers[0] : `${c.invoiceNumbers.length} invoices`;
  // The override is converted back at the invoice's rate: round it so 53,998 reads as the sheet's 54,000.
  return `The MRR sheet counts about ${fmt(Math.round(c.overrideInDocument / 100) * 100)} for the ${c.termMonths}-month term; ${docs} ${c.invoiceNumbers.length === 1 ? 'bills' : 'bill'} ${fmt(c.invoicedInDocument)} (${pct}% ${more ? 'more' : 'less'}). One of them is wrong: check the MRR sheet.`;
}
